<template>

  <div>
    <heading :level="2" class="mb-6">{{ $t('Multiple choice response set') }}</heading>

    <table
      v-if="temporaryResponseSet.responses.length > 0"
      class="table w-full no-borders"
      cellpadding="0"
      cellspacing="0"
    >
      <thead>
      <tr>
        <th>{{ $t('Label') }}</th>
        <th style="width:80px;">{{ $t('Color') }}</th>
        <th style="width:80px;">
          <div class="flex items-center page-title-tooltiped">
            {{ $t('Score') }}

            <icon-info-circle :text="$t('feature-explanation.score-points')" />
          </div>
        </th>
        <th style="width:80px;">
          <div class="flex items-center page-title-tooltiped">
            {{ $t('Negative') }}

            <icon-info-circle :text="$t('feature-explanation.negative')" />
          </div>
        </th>
        <th>&nbsp;</th>
      </tr>
      </thead>
      <tbody>
      <tr v-for="(response, index) in temporaryResponseSet.responses" :key="response.uuid">
        <td>
          <input
            ref="responseTitle"
            :value="responseTitle(response)"
            :placeholder="responseTitlePlaceholder(response)"
            class="w-full form-input2 form-bottom-line outline-none"
            @keydown.enter.exact.prevent
            @keyup.enter.exact="onEnterPressed"
            @input="onTitleChanged(response, $event.target.value)"
          >
        </td>
        <td>
          <ColorPicker v-model="response.color" :disabled="isTranslationMode" />
        </td>
        <td>
          <input
            type="number"
            step="1"
            min="0"
            :value="response.score"
            :disabled="isTranslationMode"
            :class="{ 'opacity-50': isTranslationMode }"
            class="w-full form-input2 form-bottom-line outline-none"
            @input="handleScoreChange($event, response)"
          >
        </td>
        <td>
          <checkbox
            :checked="response.negative"
            :disabled="isTranslationMode"
            :class="{ 'opacity-50': isTranslationMode }"
            class="justify-center"
            @input="response.negative = arguments[0]"
          />
        </td>
        <td>
          <DeleteButton :disabled="isTranslationMode" @click="deleteItem(response, index)" />
        </td>
      </tr>
      </tbody>
    </table>

    <div class="mt-3 mb-5 flex items-center">
      <button
        class="btn btn-default btn-secondary mr-2"
        :disabled="isTranslationMode"
        @click="$emit('addResponseAfter', temporaryResponseSet.responses.length)"
        v-text="$t('Add item')"
      ></button>
      <span>{{ $t('or press enter') }}</span>
    </div>
  </div>

</template>

<script>
  import DeleteButton from '@/_legacy/components/DeleteButton.vue';
  import ColorPicker from '@/_legacy/components/Controls/ColorPicker/ColorPicker.vue';
  import IconInfoCircle from '@/_legacy/components/Icons/IconInfoCircle';
  import { Button as BButton } from '@/ui/components/Button';
  import { t } from '@/ui/i18n/i18n';
  import { eventBus } from '@/_legacy/eventBus';
  import { useManualTranslationsStore } from '@/stores/checklist/manualTranslations';

  export default {
    components: {
      DeleteButton,
      ColorPicker,
      IconInfoCircle,
      BButton
    },
    props: {
      temporaryResponseSet: {
        type: Object,
        required: true,
      },
    },
    data() {
      const store = useManualTranslationsStore();
      const manualTitles = new Map();

      if (this.temporaryResponseSet.responses?.length > 0) {
        this.temporaryResponseSet.responses.forEach(
          response => manualTitles.set(response.uuid, store.getTitle(response.node_id, response.uuid)),
        );
      }

      return {
        manualTitles,
      };
    },
    computed: {
      colors() {
        return [
          {
            value: '#4AD862',
            label: '<span class="rounded-lg" style="width:24px; height:24px; display:inline-block; background: #4AD862"></span>',
          },
          {
            value: '#FDAC43',
            label: '<span class="rounded-lg" style="width:24px; height:24px; display:inline-block; background: #FDAC43"></span>',
          },
          {
            value: '#FF6E44',
            label: '<span class="rounded-lg" style="width:24px; height:24px; display:inline-block; background: #FF6E44"></span>',
          },
          {
            value: '#777777',
            label: '<span class="rounded-lg" style="width:24px; height:24px; display:inline-block; background: #777777"></span>',
          },
          {
            value: '#1fb6ff',
            label: '<span class="rounded-lg" style="width:24px; height:24px; display:inline-block; background: #1fb6ff"></span>',
          },
          {
            value: '#BA31ED',
            label: '<span class="rounded-lg" style="width:24px; height:24px; display:inline-block; background: #BA31ED"></span>',
          },
          {
            value: '#1BE7FF',
            label: '<span class="rounded-lg" style="width:24px; height:24px; display:inline-block; background: #1BE7FF"></span>',
          },
          {
            value: '#FF49DB',
            label: '<span class="rounded-lg" style="width:24px; height:24px; display:inline-block; background: #FF49DB"></span>',
          },
          {
            value: '#4474FF',
            label: '<span class="rounded-lg" style="width:24px; height:24px; display:inline-block; background: #4474FF"></span>',
          },
          {
            value: '#60D956',
            label: '<span class="rounded-lg" style="width:24px; height:24px; display:inline-block; background: #60D956"></span>',
          },
          {
            value: '#7C69F8',
            label: '<span class="rounded-lg" style="width:24px; height:24px; display:inline-block; background: #7C69F8"></span>',
          },
          {
            value: '#60D2C9',
            label: '<span class="rounded-lg" style="width:24px; height:24px; display:inline-block; background: #60D2C9"></span>',
          },
        ];
      },
      responseSetItemsInUse() {
        return this.$store.getters['checklist_builder/responseSetItemsInUse'];
      },
      isTranslationMode() {
        return this.$store.getters['checklist_builder/edit_response_set_modal'].isTranslationMode;
      },
    },
    methods: {
      deleteItem(response, index) {
        if (this.responseSetItemsInUse?.includes(response.uuid)) {
          eventBus.$emit('error', t('This response set option is in use, it can not be deleted!'));
        } else {
          this.$emit('deleteResponse', index);
        }
      },
      handleScoreChange(event, response) {
        const score = event.target.value;
        if (score.trim() === '') {
          response.score = null;
        } else if (!isNaN(parseInt(score))) {
          response.score = parseInt(score);
        }
      },
      responseTitle(response) {
        return this.isTranslationMode
          ? (this.manualTitles.get(response.uuid) ?? response.title_translated)
          : response.title;
      },
      responseTitlePlaceholder(response) {
        return this.isTranslationMode && response.title ? response.title : t('Enter item here');
      },
      onEnterPressed(index) {
        if (this.isTranslationMode) {
          return;
        }

        this.$emit('addResponseAfter', index);
      },
      onTitleChanged(response, value) {
        if (this.isTranslationMode) {
          this.manualTitles.set(response.uuid, value);
          this.$emit('manualTitlesChanged', this.manualTitles);

          return;
        }

        this.$set(response, 'title', value);
      },
    },
  };
</script>
